import { useEffect, useRef, useState } from "react";
import { Sparkles, Wand2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import { api, BACKEND, getAuthToken } from "@/lib/api";
import { AI_FOCUS } from "@/data/constants";
import { monthKey } from "@/lib/money";
import { t, getLang } from "@/lib/i18n";

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function md(t) {
  const out = [];
  let ul = false;
  esc(t).split("\n").forEach((l) => {
    const line = l.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/^#+\s*/, "");
    if (/^\s*[-*•]\s+/.test(line)) { if (!ul) { out.push("<ul>"); ul = true; } out.push(`<li>${line.replace(/^\s*[-*•]\s+/, "")}</li>`); }
    else { if (ul) { out.push("</ul>"); ul = false; } if (line.trim()) out.push(`<p>${line}</p>`); }
  });
  if (ul) out.push("</ul>");
  return out.join("");
}

export default function AiTips() {
  const { anchor } = useData();
  const [focus, setFocus] = useState("Overall");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ctl = useRef(null);

  useEffect(() => { api.get("/ai/tips/last").then(({ data }) => data.text && setText(data.text)).catch(() => {}); return () => ctl.current?.abort(); }, []);

  const go = async () => {
    setBusy(true); setErr(""); setText("");
    ctl.current = new AbortController();
    try {
      const r = await fetch(`${BACKEND}/api/ai/tips`, {
        method: "POST", signal: ctl.current.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ month: monthKey(anchor), focus: focus === "Overall" ? "" : focus, lang: getLang() }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "AI tips are unavailable right now."); }
      const rd = r.body.getReader(), dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await rd.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          const j = JSON.parse(d);
          if (j.error) setErr(j.error); else setText((t) => t + j.t);
        }
      }
    } catch (e) { if (e.name !== "AbortError") setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <section className="mm-card mm-ai-border mm-rise" data-testid="ai-spending-tips-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="mm-h2 flex items-center gap-2 text-lg"><Sparkles className="h-5 w-5 text-[var(--ac)]" /> {t("Smart money tips")}</h2>
        <span className="rounded-full bg-[var(--bg)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--mu)]">Free insights</span>
      </div>
      <div className="no-scrollbar -mx-1 mb-4 flex gap-2 overflow-x-auto px-1">
        {AI_FOCUS.map((f) => <button key={f} className={`mm-chip ${focus === f ? "on" : ""}`} onClick={() => setFocus(f)} data-testid={`ai-focus-${f.replace(/\s+/g, "-").toLowerCase()}`}>{t(f)}</button>)}
      </div>
      <div className={`mm-md min-h-[64px] text-[15px] leading-relaxed ${busy ? "mm-caret" : ""}`} data-testid="ai-tips-output" dangerouslySetInnerHTML={{ __html: text ? md(text) : busy ? "" : `<p class="mu">${t("See automatic spending observations based on your own income and expenses. No paid AI service required.")}</p>` }} />
      {err && <p className="mt-2 text-sm text-[var(--dg)]" data-testid="ai-tips-error">{err}</p>}
      <button className="mm-btn mt-4" onClick={go} disabled={busy} data-testid="ai-spending-tips-generate-button"><Wand2 className="h-4 w-4" /> {busy ? t("Thinking…") : text ? t("New tips") : t("Get my tips")}</button>
    </section>
  );
}
